import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import { useCreateUser } from '../../hooks/useUsers.ts';
import { useRbacRoles } from '../../hooks/useRbac.ts';
import { TextInput, SelectInput, PasswordInput } from '../common/inputs/index.ts';
import type { CreateUserRequest } from '../../types.ts';
import { fetchOrgAdminRoles } from '../../../request/membership/orgUsers.api.ts';
import {
  DEFAULT_ORG_ROLE,
  ORG_ROLE_OPTIONS,
} from '../../orgRoles.ts';

interface CreateUserFormValues {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  username: string;
  role: string;
}

interface CreateUserFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Organization selected on the page; user will be created in this organization. */
  organizationId: string;
  /** Override create API (e.g. org admin product route). Defaults to platform admin POST /api/admin/users. */
  createUserRequest?: (payload: CreateUserRequest) => Promise<unknown>;
}

const createUserSchema = yup
  .object({
    email: yup.string().required('Email is required').email('Invalid email address'),
    first_name: yup
      .string()
      .required('First name is required')
      .min(2, 'First name must be at least 2 characters'),
    last_name: yup
      .string()
      .required('Last name is required')
      .min(2, 'Last name must be at least 2 characters'),
    password: yup
      .string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters'),
    username: yup
      .string()
      .required('Username is required')
      .min(3, 'Username must be at least 3 characters'),
    role: yup.string().required('Role is required'),
  })
  .required();

export const CreateUserForm: React.FC<CreateUserFormProps> = ({
  onSuccess,
  onCancel,
  organizationId,
  createUserRequest,
}) => {
  const isOrgPath = Boolean(createUserRequest);
  const { data: adminRoles = [], isLoading: adminRolesLoading } = useRbacRoles({
    enabled: !isOrgPath,
  });
  const { data: orgRoles = [], isLoading: orgRolesLoading } = useQuery({
    queryKey: ['org-admin-roles'],
    queryFn: fetchOrgAdminRoles,
    enabled: isOrgPath,
  });

  const roles = isOrgPath ? orgRoles : adminRoles;
  const rolesLoading = isOrgPath ? orgRolesLoading : adminRolesLoading;

  const roleOptions = useMemo(() => {
    if (roles.length === 0) {
      return ORG_ROLE_OPTIONS;
    }
    return roles.map((role) => ({
      value: role.key,
      label: role.name,
    }));
  }, [roles]);

  const createUserMutation = useCreateUser();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserFormValues>({
    resolver: yupResolver(createUserSchema),
    defaultValues: {
      role: DEFAULT_ORG_ROLE,
    },
  });

  const onSubmit = async (data: CreateUserFormValues) => {
    const payload: CreateUserRequest = {
      ...data,
      role: data.role,
      organization_id: organizationId,
    };

    try {
      if (createUserRequest) {
        await createUserRequest(payload);
      } else {
        await createUserMutation.mutateAsync(payload);
      }
      reset();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to create user:', error);
      // Error handled by hook
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <TextInput
        id="username"
        label="Username"
        required
        placeholder="johndoe"
        error={errors.username?.message}
        {...register('username')}
      />
      <TextInput
        id="email"
        type="email"
        label="Email"
        required
        placeholder="john.doe@example.com"
        error={errors.email?.message}
        {...register('email')}
      />
      <div className="grid grid-cols-2 gap-4">
        <TextInput
          id="first_name"
          label="First Name"
          required
          placeholder="John"
          error={errors.first_name?.message}
          {...register('first_name')}
        />
        <TextInput
          id="last_name"
          label="Last Name"
          required
          placeholder="Doe"
          error={errors.last_name?.message}
          {...register('last_name')}
        />
      </div>
      <PasswordInput
        id="password"
        label="Password"
        required
        placeholder="••••••••"
        error={errors.password?.message}
        {...register('password')}
      />
      <SelectInput
        id="role"
        label="Role"
        required
        placeholder={rolesLoading ? 'Loading roles...' : 'Select role'}
        options={roleOptions}
        disabled={rolesLoading}
        error={errors.role?.message}
        {...register('role')}
      />
      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting || rolesLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Creating...' : 'Create User'}
        </button>
      </div>
    </form>
  );
};
